import { sb } from '@/lib/supabase'
import type {
  EvaluationCategory,
  EvaluationCriterion,
  EvaluationTemplate,
  SelfAssessment,
  SelfAssessmentScore,
} from '@/lib/types'

/**
 * The individual SWEBOK self-assessment (migrations/0067, questions in 0068).
 *
 * The rules a person's page and the coach's page share live here, so the two
 * cannot disagree about what "an area's score" or "work on this first" means.
 */

export const SELF_ASSESSMENT_SLUG = 'swebok-self-assessment'

export interface SelfAssessmentCatalog {
  template: EvaluationTemplate
  categories: EvaluationCategory[]
  criteria: EvaluationCriterion[]
}

/** The template, its areas and its questions, in display order. */
export async function loadSelfAssessmentCatalog(): Promise<SelfAssessmentCatalog | null> {
  const tpl = await sb()
    .from('evaluation_templates')
    .select('*')
    .is('team_id', null)
    .eq('slug', SELF_ASSESSMENT_SLUG)
    .maybeSingle()
  if (tpl.error) throw tpl.error
  if (!tpl.data) return null
  const cats = await sb()
    .from('evaluation_categories')
    .select('*')
    .eq('template_id', tpl.data.id)
    .order('display_order')
  if (cats.error) throw cats.error
  const crits = await sb()
    .from('evaluation_criteria')
    .select('*')
    .in('category_id', (cats.data ?? []).map((c) => c.id))
    .order('display_order')
  if (crits.error) throw crits.error
  return {
    template: tpl.data as EvaluationTemplate,
    categories: (cats.data ?? []) as EvaluationCategory[],
    criteria: (crits.data ?? []) as EvaluationCriterion[],
  }
}

export interface AreaResult {
  category: EvaluationCategory
  /** Mean of the answered questions in this area; null if none answered. */
  average: number | null
  answered: number
  total: number
  /** This area's questions with their answers, lowest score first. */
  questions: Array<{ criterion: EvaluationCriterion; score: number | null; notes: string | null }>
}

/** One run's answers, rolled up by area. Tests `!= null`, never truthiness. */
export function areaResults(
  catalog: SelfAssessmentCatalog,
  scores: Pick<SelfAssessmentScore, 'criterion_id' | 'score' | 'notes'>[],
): AreaResult[] {
  const byCriterion = new Map(scores.map((s) => [s.criterion_id, s]))
  return catalog.categories.map((category) => {
    const questions = catalog.criteria
      .filter((c) => c.category_id === category.id)
      .map((criterion) => {
        const s = byCriterion.get(criterion.id)
        return { criterion, score: s != null ? Number(s.score) : null, notes: s?.notes ?? null }
      })
    const answered = questions.filter((q) => q.score != null)
    const average = answered.length
      ? answered.reduce((sum, q) => sum + (q.score as number), 0) / answered.length
      : null
    questions.sort((a, b) => (a.score ?? Infinity) - (b.score ?? Infinity))
    return { category, average, answered: answered.length, total: questions.length, questions }
  })
}

/**
 * What to work on first: the lowest-scoring areas, and within each the
 * questions at or below "Working". Ties keep catalog order, so the advice is
 * stable from one visit to the next.
 */
export function focusAreas<T extends { average: number | null }>(results: T[], count = 3): T[] {
  return results
    .filter((r) => r.average != null)
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (a.r.average as number) - (b.r.average as number) || a.i - b.i)
    .slice(0, count)
    .map(({ r }) => r)
}

/** The strongest areas — what someone could teach. */
export function strengths<T extends { average: number | null }>(results: T[], count = 2): T[] {
  return results
    .filter((r) => r.average != null)
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (b.r.average as number) - (a.r.average as number) || a.i - b.i)
    .slice(0, count)
    .map(({ r }) => r)
}

/** Questions that name AI — for the "how is AI going" line on the results. */
export function isAiQuestion(c: Pick<EvaluationCriterion, 'name'>): boolean {
  return /\bAI\b/.test(c.name)
}

export interface PersonStanding<M> {
  member: M
  /** Their open run, if they are part-way through one (a first take or a retake). */
  open: SelfAssessment | undefined
  openAnswered: number
  /** Their most recent FINISHED run — the one the aggregate counts. */
  latest: SelfAssessment | undefined
  latestResults: AreaResult[] | null
}

export interface AreaAggregate {
  category: EvaluationCategory
  average: number | null
  min: number | null
  max: number | null
  /** People whose latest finished run answered this area. */
  answered: number
}

/**
 * The coach's view of a team: each person's standing, and per area the mean of
 * each person's area average across their LATEST FINISHED run — so retaking
 * counts once, and a half-answered run (which reads lower than it is) not at all.
 */
export function teamPicture<M extends { user_id: string | null }>(
  catalog: SelfAssessmentCatalog,
  members: M[],
  runs: SelfAssessment[],
  scores: SelfAssessmentScore[],
): { people: PersonStanding<M>[]; aggregate: AreaAggregate[] } {
  const people = members.map((member) => {
    const mine = runs.filter((r) => r.user_id === member.user_id)
    const open = mine.find((r) => r.status === 'in_progress')
    const latest = mine
      .filter((r) => r.status === 'complete')
      .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))[0]
    return {
      member,
      open,
      openAnswered: open ? scores.filter((s) => s.assessment_id === open.id).length : 0,
      latest,
      latestResults: latest ? areaResults(catalog, scores.filter((s) => s.assessment_id === latest.id)) : null,
    }
  })
  const finished = people.filter((p) => p.latestResults)
  const aggregate = finished.length
    ? catalog.categories.map((category) => {
        const vals = finished
          .map((p) => p.latestResults!.find((r) => r.category.id === category.id)?.average)
          .filter((v): v is number => v != null)
        return {
          category,
          average: mean(vals),
          min: vals.length ? Math.min(...vals) : null,
          max: vals.length ? Math.max(...vals) : null,
          answered: vals.length,
        }
      })
    : []
  return { people, aggregate }
}

export function mean(xs: number[]): number | null {
  return xs.length ? xs.reduce((s, v) => s + v, 0) / xs.length : null
}
